import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { AlertTriangle, Zap, Clock, ChevronRight, X } from "lucide-react";

// ─── usePlanStatus hook ────────────────────────────────────────────────────
export function usePlanStatus() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    const delays = [0, 1000, 2500];
    for (let i = 0; i < delays.length; i++) {
      if (delays[i] > 0) await new Promise(r => setTimeout(r, delays[i]));
      try {
        const r = await api.get("/auth/plan-status");
        setStatus(r.data);
        setLoading(false);
        return;
      } catch {
        if (i === delays.length - 1) {
          setStatus(null);
          setLoading(false);
        }
      }
    }
  };

  useEffect(() => { refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return { status, loading, refresh };
}

// ─── TrialBanner ───────────────────────────────────────────────────────────
// Shows a top-of-page amber bar during free trial — DESKTOP ONLY (hidden on mobile via CSS)
export function TrialBanner({ status, onDismiss }) {
  const [dismissed, setDismissed] = useState(false);

  if (!status || dismissed) return null;
  const { trial, plan } = status;
  if (plan !== "free" || !trial?.in_trial) return null;

  const urgent = trial.days_left <= 2;

  const handleDismiss = () => {
    setDismissed(true);
    onDismiss?.();
  };

  return (
    // hidden on mobile — mobile users see TrialMobilePopup instead
    <div
      className={`w-full hidden sm:block ${urgent ? "bg-red-600 text-white" : "bg-amber-400 text-zinc-900"}`}
      data-testid="trial-banner"
    >
      <div className="flex items-center justify-between gap-2 px-3 sm:px-5 py-2 sm:py-2.5 flex-wrap">
        {/* Left: icon + text */}
        <div className="flex items-center gap-2 min-w-0 flex-1" style={{ minWidth: 0 }}>
          <Clock size={14} className="shrink-0" />
          <div className="flex flex-col sm:flex-row sm:items-center sm:gap-1 min-w-0 text-xs sm:text-sm font-medium leading-tight">
            {urgent ? (
              <>
                <strong className="shrink-0">
                  Trial ends in {trial.days_left} day{trial.days_left !== 1 ? "s" : ""}.
                </strong>
                <span className="text-xs sm:text-sm opacity-90">
                  Upgrade to keep your alerts active.
                </span>
              </>
            ) : (
              <>
                <strong className="shrink-0">
                  {trial.days_left} day{trial.days_left !== 1 ? "s" : ""} left on free trial.
                </strong>
                <span className="text-xs sm:text-sm opacity-80">
                  Up to {status.limits?.max_jobs} jobs &amp; {status.limits?.max_permits_per_job} permits/job.
                </span>
              </>
            )}
          </div>
        </div>

        {/* Right: upgrade button + dismiss */}
        <div className="flex items-center gap-1.5 shrink-0">
          <a
            href="/account?tab=billing"
            className={`flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm whitespace-nowrap
              ${urgent ? "bg-white text-red-700 hover:bg-red-50" : "bg-zinc-900 text-white hover:bg-zinc-700"}`}
          >
            Upgrade <ChevronRight size={11} />
          </a>
          <button
            onClick={handleDismiss}
            className="opacity-60 hover:opacity-100 transition-opacity p-0.5"
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── TrialExpiredBanner ────────────────────────────────────────────────────
// Shown when trial has expired and user hasn't subscribed — DESKTOP ONLY
export function TrialExpiredBanner({ status }) {
  if (!status) return null;
  const { trial, plan } = status;
  if (plan !== "free" || !trial?.expired || trial?.in_trial) return null;

  return (
    // hidden on mobile — mobile users see TrialMobilePopup instead
    <div
      className="w-full bg-zinc-900 text-white hidden sm:block"
      data-testid="trial-expired-banner"
    >
      <div className="flex items-center justify-between gap-2 px-3 sm:px-5 py-2 sm:py-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0 flex-1" style={{ minWidth: 0 }}>
          <AlertTriangle size={14} className="text-amber-400 shrink-0" />
          <div className="flex flex-col sm:flex-row sm:gap-1 min-w-0 leading-tight">
            <strong className="text-xs sm:text-sm shrink-0">Free trial ended.</strong>
            <span className="text-xs sm:text-sm text-zinc-400">
              View only — subscribe to add new jobs.
            </span>
          </div>
        </div>
        <a
          href="/account?tab=billing"
          className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-sm bg-amber-400 text-zinc-900 hover:bg-amber-300 shrink-0 whitespace-nowrap"
        >
          Choose plan <ChevronRight size={11} />
        </a>
      </div>
    </div>
  );
}

// ─── TrialMobilePopup ─────────────────────────────────────────────────────
// Mobile-only popup shown on every login session while user is on free trial
// (also shows for expired trial on mobile). Uses sessionStorage so it reappears
// on every new session but can be dismissed with the X.
export function TrialMobilePopup({ status }) {
  const SESSION_KEY = "trial_popup_dismissed_v2";
  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    // Wait until status is loaded
    if (!status) return;

    const { trial, plan } = status;

    // Show for free users that are either in trial OR have expired trial
    const shouldShow = plan === "free" && (trial?.in_trial || trial?.expired);
    if (!shouldShow) return;

    // Use JS width check instead of CSS breakpoint — Samsung Galaxy and other
    // Android phones report a scaled window.innerWidth that can exceed Tailwind's
    // sm breakpoint (640px) even on a physically small screen, causing sm:hidden
    // to incorrectly suppress the popup. window.screen.width / devicePixelRatio
    // gives the actual CSS pixel width the browser uses for layout.
    const cssWidth = window.screen.width / (window.devicePixelRatio || 1);
    const isMobile = cssWidth < 640 || window.innerWidth < 640;
    if (!isMobile) return;

    // Show if not dismissed this session
    const alreadyDismissed = sessionStorage.getItem(SESSION_KEY);
    if (alreadyDismissed) return;

    // Small delay so it doesn't flash during initial page paint
    const t = setTimeout(() => {
      setMounted(true);
      // Trigger animation on next frame
      requestAnimationFrame(() => setVisible(true));
    }, 800);
    return () => clearTimeout(t);
  }, [status]);

  const dismiss = () => {
    setVisible(false);
    sessionStorage.setItem(SESSION_KEY, "1");
    // Remove from DOM after animation
    setTimeout(() => setMounted(false), 300);
  };

  if (!mounted || !status) return null;

  const { trial, limits, plan } = status;
  const isExpired = plan === "free" && trial?.expired && !trial?.in_trial;
  const urgent = isExpired || (trial?.days_left ?? 0) <= 2;
  const maxJobs = limits?.max_jobs ?? 2;
  const maxPermits = limits?.max_permits_per_job ?? 5;

  const daysText = isExpired
    ? "Your free trial has ended"
    : trial?.days_left === 0
    ? "Today is your last day"
    : trial?.days_left === 1
    ? "1 day left on your free trial"
    : `${trial?.days_left} days left on your free trial`;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center pb-6 px-4"
      style={{
        background: "rgba(9,9,11,0.55)",
        backdropFilter: "blur(2px)",
        opacity: visible ? 1 : 0,
        transition: "opacity 0.25s ease",
      }}
      data-testid="trial-mobile-popup"
      onClick={(e) => { if (e.target === e.currentTarget) dismiss(); }}
    >
      <div
        className="w-full max-w-sm bg-white rounded-sm shadow-2xl overflow-hidden"
        style={{
          transform: visible ? "translateY(0) scale(1)" : "translateY(24px) scale(0.97)",
          transition: "transform 0.28s cubic-bezier(0.34,1.56,0.64,1)",
        }}
      >
        {/* Colour bar at top */}
        <div className={`h-1.5 w-full ${urgent ? "bg-red-500" : "bg-amber-400"}`} />

        <div className="p-5">
          {/* Header row */}
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-2.5">
              <div className={`w-8 h-8 rounded-sm flex items-center justify-center shrink-0 ${urgent ? "bg-red-100" : "bg-amber-100"}`}>
                <Clock size={15} className={urgent ? "text-red-600" : "text-amber-700"} />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.12em] text-zinc-400">Free Trial</p>
                <p className={`font-display text-base font-bold tracking-tight leading-tight ${urgent ? "text-red-700" : "text-zinc-900"}`}>
                  {daysText}
                </p>
              </div>
            </div>
            <button
              onClick={dismiss}
              aria-label="Dismiss"
              className="mt-0.5 text-zinc-400 hover:text-zinc-700 shrink-0 p-0.5 -mr-0.5 transition-colors"
            >
              <X size={18} />
            </button>
          </div>

          {isExpired ? (
            <p className="text-sm text-zinc-600 leading-relaxed mb-4">
              Your free trial has ended. Subscribe to a plan to continue adding jobs, tracking permits, and receiving alerts.
            </p>
          ) : (
            <p className="text-sm text-zinc-600 leading-relaxed mb-4">
              {urgent
                ? "Your free trial is almost over. Upgrade now to keep your permit alerts and job tracking running without interruption."
                : `You have ${trial?.days_left} days left — up to ${maxJobs} jobs & ${maxPermits} permits/job. Upgrade before it ends to keep everything running.`}
            </p>
          )}

          {/* Mini plan pills */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            {[
              { name: "Starter", price: "$19", tag: "5 jobs" },
              { name: "Pro",     price: "$39", tag: "25 jobs", hot: true },
              { name: "Agency",  price: "$89", tag: "Unlimited" },
            ].map(p => (
              <div
                key={p.name}
                className={`rounded-sm border p-2.5 flex flex-col gap-0.5 ${p.hot ? "border-zinc-900 bg-zinc-950" : "border-zinc-200"}`}
              >
                <span className={`text-[10px] font-bold uppercase tracking-wider ${p.hot ? "text-amber-400" : "text-zinc-500"}`}>{p.name}</span>
                <span className={`text-sm font-bold ${p.hot ? "text-white" : "text-zinc-900"}`}>{p.price}<span className="text-[10px] font-normal">/mo</span></span>
                <span className={`text-[10px] ${p.hot ? "text-zinc-400" : "text-zinc-500"}`}>{p.tag}</span>
              </div>
            ))}
          </div>

          {/* CTA */}
          <a
            href="/account?tab=billing"
            onClick={dismiss}
            className="flex items-center justify-center gap-2 w-full py-3 bg-zinc-950 text-white text-sm font-bold rounded-sm hover:bg-zinc-800 active:bg-zinc-700 transition-colors"
          >
            <Zap size={14} /> View Plans &amp; Upgrade
          </a>
          <button
            onClick={dismiss}
            className="w-full mt-2 py-1.5 text-xs text-zinc-400 hover:text-zinc-600 transition-colors"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}

// Small inline usage bar — e.g. "3 / 5 jobs used"
export function UsageMeter({ current, max, label, className = "" }) {
  if (!max || max >= 999) return null;
  const pct = Math.min(100, Math.round((current / max) * 100));
  const near = pct >= 80;
  const full = pct >= 100;

  return (
    <div className={`flex flex-col gap-1 ${className}`} data-testid="usage-meter">
      <div className="flex items-center justify-between text-xs text-zinc-500">
        <span className="uppercase tracking-widest font-semibold">{label}</span>
        <span className={full ? "text-red-600 font-bold" : near ? "text-amber-600 font-semibold" : ""}>
          {current} / {max}
        </span>
      </div>
      <div className="h-1.5 w-full bg-zinc-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-300
            ${full ? "bg-red-500" : near ? "bg-amber-400" : "bg-zinc-900"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// ─── UpgradePrompt ────────────────────────────────────────────────────────
export function UpgradePrompt({ message, onClose, isExpired = false }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      data-testid="upgrade-prompt"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="bg-white border border-zinc-200 rounded-sm shadow-xl max-w-md w-full mx-4 p-6">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="bg-amber-400 p-1.5 rounded-sm">
              <Zap size={16} className="text-zinc-900" />
            </div>
            <span className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">
              {isExpired ? "Trial Ended" : "Plan Limit Reached"}
            </span>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700">
              <X size={16} />
            </button>
          )}
        </div>

        {/* Message */}
        <p className="text-zinc-800 text-sm mb-5 leading-relaxed">{message}</p>

        {/* Plan cards */}
        <div className="grid grid-cols-3 gap-2 mb-5">
          {[
            { name: "Starter", price: "$19", jobs: "5 jobs", permits: "20 permits/job" },
            { name: "Pro", price: "$39", jobs: "25 jobs", permits: "Unlimited permits", highlight: true },
            { name: "Agency", price: "$89", jobs: "Unlimited", permits: "Unlimited permits" },
          ].map((p) => (
            <div
              key={p.name}
              className={`border rounded-sm p-3 flex flex-col gap-1
                ${p.highlight ? "border-zinc-900 bg-zinc-950 text-white" : "border-zinc-200"}`}
            >
              <span className={`text-xs font-bold uppercase tracking-widest ${p.highlight ? "text-amber-400" : "text-zinc-500"}`}>
                {p.name}
              </span>
              <span className={`text-lg font-bold ${p.highlight ? "text-white" : "text-zinc-900"}`}>
                {p.price}<span className="text-xs font-normal">/mo</span>
              </span>
              <span className={`text-xs ${p.highlight ? "text-zinc-300" : "text-zinc-500"}`}>{p.jobs}</span>
              <span className={`text-xs ${p.highlight ? "text-zinc-300" : "text-zinc-500"}`}>{p.permits}</span>
            </div>
          ))}
        </div>

        {/* CTA */}
        <a
          href="/account?tab=billing"
          className="flex items-center justify-center gap-2 w-full py-2.5 bg-zinc-950 text-white text-sm font-semibold rounded-sm hover:bg-zinc-800 transition-colors"
        >
          <Zap size={14} /> View Plans &amp; Upgrade
        </a>
        {onClose && (
          <button
            onClick={onClose}
            className="w-full mt-2 py-2 text-xs text-zinc-400 hover:text-zinc-700"
          >
            Maybe later
          </button>
        )}
      </div>
    </div>
  );
}
